import { initSentryBrowser, initSentryVue } from "@/helpers/sentry/sentry";

import { createApp } from "vue";
import "vue-virtual-scroller/index.css";
import App from "./App.vue";

import { initializeI18n } from "@/i18n/vueI18n";

import { registerFlutterFunctions } from "@/helpers/flutter/register";
import { isRunningInFlutter } from "@/helpers/flutter/capabilities";
import { sendLanguageToFlutter } from "@/helpers/flutter/features/language";

// Import Pinia
import { createPinia, setActivePinia } from "pinia";
import type { BeforeInstallPromptEvent } from "@/types/events/before-install-prompt-event";
import { useDarkModeStore } from "@/stores/modules/darkmode/darkmode";
import { useAppStore } from "@/stores/modules/app/app";
import { usePromptsStore } from "@/stores/modules/prompts/prompts";

import router from "@/router/router";
import { clearOldCachedImages } from "@/helpers/cache/image-cache";

registerFlutterFunctions();
initSentryBrowser();
const vueI18nInstance = initializeI18n();

// Notify Flutter wrapper of user's active language on boot
const bootLanguage =
  (vueI18nInstance.global.locale as any)?.value ||
  localStorage.getItem("locale") ||
  "en";
sendLanguageToFlutter(bootLanguage);

// Create Pinia instance and set it as active immediately
const pinia = createPinia();
setActivePinia(pinia);

// Initialize dark mode after Pinia is set up
const darkModeStore = useDarkModeStore();
darkModeStore.initDarkMode();

// Create Vue app and register Pinia
const vueInstance = createApp(App);
vueInstance.use(pinia);

// Register other plugins and mount
vueInstance.use(router).use(vueI18nInstance).mount("#app");

initSentryVue(vueInstance);

// Initialize Pinia app store after mounting - this will set up auth listener and fetch data
const appStore = useAppStore();
appStore.initializeApp();

// Clean up expired cached images on app startup (non-blocking)
clearOldCachedImages().catch((error) => {
  console.error("Failed to clean up expired cache on startup:", error);
});

// Initialize prompts store for PWA install prompt
const promptsStore = usePromptsStore();
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  promptsStore.setPwaInstallPrompt(e as BeforeInstallPromptEvent);
});

//Clicking on a notification when the app is in the background
if (!isRunningInFlutter()) {
  try {
    navigator?.serviceWorker?.addEventListener("message", (message) => {
      if (
        message?.data?.messageType === "notification-clicked" &&
        message?.data?.notification?.click_action
      ) {
        router.push(message.data.notification.click_action);
      }
    });
  } catch {
    //
  }
}
